// ProfilePreviewCard component for displaying influencer profile data
// Premium glassy design with overlapping avatar and gradient banner
// Props: { avatarUrl, displayName, location, bio, niches, totalFollowers, engagementRate, platformBreakdown }
// 
// QA Test Cases:
// - If user only entered name + bio → shows header + bio; hides location, socials, all stats blocks
// - If user entered only followers → shows just the Following & Engagement block with the followers row
// - If niches empty → Niches block hidden entirely
// - No "—" placeholders appear; spacing collapses naturally

import React from 'react';
import { formatCompact, formatPct } from '../utils/num.js';

const ProfilePreviewCard = ({
  avatarUrl,
  displayName,
  handle,
  location,
  bio,
  niches = [],
  totalFollowers,
  engagementRate,
  platformBreakdown = [],
  socialLinks = {}
}) => {
  // Helper functions for conditional rendering and formatting
  const isNonEmpty = v =>
    v !== undefined && v !== null && String(v).trim() !== "";

  const hasAny = obj =>
    obj && Object.values(obj).some(val => {
      if (Array.isArray(val)) return val.length > 0;
      return isNonEmpty(val);
    });

  // Compute derived display values
  const totalFollowersDisplay = formatCompact(totalFollowers);
  const engagementDisplay = formatPct(engagementRate);

  // Determine what sections have data
  const hasFollowing = isNonEmpty(totalFollowersDisplay) || isNonEmpty(engagementDisplay);
  const hasAnalytics = false; // No analytics fields currently provided
  const hasNiches = Array.isArray(niches) && niches.length > 0;
  const hasAnyStats = hasFollowing || hasAnalytics || hasNiches;
  
  // Helper function to detect platform from URL
  const getPlatformFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('instagram.com')) return 'instagram';
    if (lowerUrl.includes('tiktok.com')) return 'tiktok';
    if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'x';
    return null;
  };

  // Get social links with their platforms
  const socialLinksWithPlatforms = Object.entries(socialLinks)
    .map(([key, url]) => ({
      key,
      url,
      platform: getPlatformFromUrl(url)
    }))
    .filter(link => link.platform && link.url.trim());

  const hasSocialLinks = socialLinksWithPlatforms.length > 0;

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Main Profile Card */}
      <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg border border-gray-100">
        {/* Light Gradient Header */}
        <div className="h-20 bg-gradient-to-r from-blue-50 to-white"></div>
        
        {/* Profile Content */}
        <div className="px-6 pb-6">
          {/* Top Section: Profile Image + Name + Location */}
          <div className="flex flex-col items-center text-center -mt-12 mb-6">
            {/* Profile Image */}
            <div className="relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={`${displayName || 'User'} avatar`}
                  className="h-20 w-20 rounded-full ring-4 ring-white shadow-lg object-cover"
                />
              ) : (
                <div className="h-20 w-20 rounded-full ring-4 ring-white shadow-lg bg-gray-200 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            
            {/* Name */}
            {isNonEmpty(displayName) && (
              <h2 className="text-2xl font-bold text-gray-900 mt-4">
                {displayName}
              </h2>
            )}
            
            {/* Location */}
            {isNonEmpty(location) && (
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {location}
              </p>
            )}
          </div>

          {/* Middle Section: Bio */}
          {isNonEmpty(bio) && (
            <div className="mb-6">
              <p className="text-gray-700 leading-relaxed text-center">
                {bio}
              </p>
            </div>
          )}

          {/* Social Links - dynamically render based on URLs */}
          {hasSocialLinks && (
            <div className="flex justify-center items-center gap-3 mt-4 mb-6">
              {socialLinksWithPlatforms.map(({ key, url, platform }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors hover:text-blue-500"
                  aria-label={`Visit ${platform} profile`}
                >
                  {platform === 'instagram' && (
                    <svg className="w-5 h-5 text-gray-600 hover:text-blue-500 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  )}
                  {platform === 'tiktok' && (
                    <svg className="w-5 h-5 text-gray-600 hover:text-blue-500 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  )}
                  {platform === 'youtube' && (
                    <svg className="w-5 h-5 text-gray-600 hover:text-blue-500 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  )}
                  {platform === 'x' && (
                    <svg className="w-5 h-5 text-gray-600 hover:text-blue-500 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  )}
                </a>
              ))}
            </div>
          )}

          {/* Bottom Section: Two Columns - Following/Engagement + Niches */}
          {hasAnyStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column: Following & Engagement */}
              {hasFollowing && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Following & Engagement</h3>
                  <div className="space-y-3">
                    {isNonEmpty(totalFollowersDisplay) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total Followers</span>
                        <span className="text-lg font-bold text-gray-900">{totalFollowersDisplay}</span>
                      </div>
                    )}
                    {isNonEmpty(engagementDisplay) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Engagement Rate</span>
                        <span className="text-lg font-bold text-gray-900">{engagementDisplay}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Right Column: Niches */}
              {hasNiches && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Niches</h3>
                  <div className="flex flex-wrap gap-2">
                    {niches.filter(isNonEmpty).map((niche, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-white border border-gray-200 text-gray-700"
                      >
                        {niche}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePreviewCard;
